/**
 * Logique pure du carrousel horizontal des idées (⟦C29 · lot B · point 4a⟧, `NouvelleVideo`).
 *
 * Volontairement séparée du composant : ces fonctions ne touchent aucun DOM, elles prennent des
 * nombres et en rendent un (ou un petit objet) — testables sans navigateur ni React Testing
 * Library (absente du client, `client/package.json`).
 *
 * ⟦C29 · lot B · point 4a, tour 3⟧ Ce fichier remplace entièrement `indexLePlusProche` du
 * correctif tour 2. BUG BLOQUANT trouvé par la vérification contradictoire (tour 2, six idées en
 * main — le cas normal, `MAIN_MAX = 6`, `verif-tour2/mv2-retour-clavier.cjs`) : dès que le rail
 * avait atteint son défilement maximal réel, la flèche ◀ et la touche ArrowLeft ne faisaient plus
 * RIEN — compteur bloqué à « 6/6 », focus bloqué sur la dernière carte, ◀ jamais désactivée.
 * `indexLePlusProche` supposait que « le défilement est au maximum » ⇒ « c'est forcément la
 * DERNIÈRE carte qui est la plus proche » — vrai seulement dans un rail où une seule carte est
 * visible à la fois. Ici le rail est large (mesuré : `offsets=[2,124,246,368,490,612]`,
 * `scrollMax=179` — la moitié des cartes tient déjà dans le cadre) : au maximum réel, la carte 3
 * (offset 246) est déjà la première visible, pas la 6. Pire : la fonction recevait, pour les
 * appels depuis les flèches/le clavier, l'offset BRUT de la carte VISÉE comme s'il s'agissait du
 * défilement courant. Dès qu'on visait n'importe laquelle des cartes 3 à 6 (leurs offsets
 * dépassent tous `scrollMax`), « au maximum ⇒ dernière carte » se déclenchait à CHAQUE appel, y
 * compris en reculant — un point fixe dont `indexBorne(cible - 1)` ne pouvait jamais sortir.
 *
 * ⟦C29 · lot B · point 4, tour 4⟧ BUG BLOQUANT trouvé par la vérification contradictoire (tour 3,
 * clic direct sur une carte hors de portée d'un pas de flèche) : `allerVersCarte` pose bien le bon
 * index tout de suite, mais lance aussi un défilement RÉEL et ANIMÉ (`rail.scrollTo({behavior:
 * "smooth"})`) quand la carte visée n'est pas déjà là où le rail se trouve. Chaque frame de cette
 * animation déclenche `onScroll` → `surDefilementRail`, qui RECALCULE l'index depuis la géométrie
 * (`indexCarteGauche`) et écrase le compteur qu'on venait de poser. Dans un rail large, plusieurs
 * cartes de fin PARTAGENT la même position de défilement maximale une fois écrêtée : la dernière
 * frame de l'animation, une fois arrivée à ce maximum, recalcule systématiquement la première de
 * ces cartes partageant le maximum (ici la carte 3), jamais celle réellement visée (ici la carte
 * 5) — reproductible à 100 %, mesuré dans `verif-tour3/suspicion-clic-lointain-mesures.json`.
 *
 * Un premier essai a tenté de détecter « l'animation est arrivée à sa position prédite » pour
 * réarmer le calcul géométrique à ce moment précis — RATÉ (mesuré, `apres-tour4/` avant le
 * correctif définitif) : le navigateur envoie souvent, une fois la position finale atteinte, un
 * DEUXIÈME `onScroll` à l'identique (règlement du `scroll-snap`, ou simplement la dernière frame
 * d'animation qui se répète) — le premier confirme l'arrivée et efface la protection, le second,
 * la protection déjà effacée, retombe dans le calcul géométrique et écrase quand même l'index.
 * `surDefilementRail` (`NouvelleVideo.tsx`) n'essaie donc plus de deviner QUAND l'animation est
 * arrivée : tant qu'aucun GESTE MANUEL (doigt, souris, molette — `surGesteManuelRail`) n'a repris
 * la main, ou qu'aucun NOUVEL appel à `allerVersCarte` n'a remplacé la cible protégée, toute frame
 * `onScroll` est ignorée, arrivée ou pas — l'index posé par `allerVersCarte` reste la seule vérité
 * tant que rien de plus légitime ne l'a supplanté.
 *
 * Deux idées, plus une fonction unique bancale :
 * - **La carte visée par une flèche/le clavier** (`indexBorne(index ± 1)`) est un simple compteur
 *   borné : avancer et reculer d'une carte, c'est `+1`/`-1`, point. Aucune géométrie là-dedans —
 *   donc aucun point fixe possible. C'est ce que suivent le compteur ET le focus pour CES deux
 *   entrées (elles disent toujours la même carte, jamais l'une en avance sur l'autre).
 * - **« Est-on vraiment au bout/au début ? »** (◀/▶ désactivées) reste une question de géométrie
 *   RÉELLE (position de défilement vs `scrollMax`), volontairement DÉCOUPLÉE du compteur : un
 *   rail large peut être déjà tout à fait au bout alors que l'index visé n'a pas atteint la
 *   dernière carte (plusieurs cartes de fin sont déjà visibles ensemble) — `limitesDefilement`
 *   répond à cette seule question, sur la position réelle (ou honnêtement PRÉDITE : `scrollTo`
 *   écrête à `scrollMax`, donc la position où le défilement va s'arrêter est `min(offset visé,
 *   scrollMax)` — jamais l'offset brut).
 * - **Le glissement au doigt** n'a pas de « carte visée » (rien n'a demandé un index précis) : il
 *   n'a que la position réelle du rail. `indexCarteGauche` en tire la carte affichée — la plus à
 *   gauche déjà rentrée dans le cadre, seule notion sensée quand plusieurs cartes sont visibles à
 *   la fois. C'est une fonction strictement croissante de la position : aucune direction n'y est
 *   un cas particulier, donc aucun blocage possible non plus par ce chemin.
 */

/** Tolérance en pixels : arrondi sous-pixel du navigateur, et le padding du rail (2px). */
const TOLERANCE_PX = 2;

export interface LimitesDefilement {
  /** Le rail est (à la tolérance près) tout à fait au début : ◀ n'a plus rien à faire. */
  debut: boolean;
  /** Le rail est (à la tolérance près) tout à fait au bout : ▶ n'a plus rien à faire. */
  fin: boolean;
}

/**
 * Est-on vraiment au début / au bout du défilement, pour une position `scrollLeft` donnée
 * (réelle, ou honnêtement prédite — voir le commentaire de tête) et un `scrollMax`
 * (`rail.scrollWidth - rail.clientWidth`) donné ?
 *
 * Un rail qui ne défile pas du tout (`scrollMax <= 0`, tout tient dans le cadre) est considéré
 * au bout ET au début à la fois : les deux flèches se désactivent, il n'y a nulle part où aller.
 */
export function limitesDefilement(scrollLeft: number, scrollMax: number): LimitesDefilement {
  const max = Math.max(0, scrollMax);
  return {
    debut: scrollLeft <= TOLERANCE_PX,
    fin: max <= TOLERANCE_PX || scrollLeft >= max - TOLERANCE_PX,
  };
}

/**
 * La carte la plus à gauche déjà rentrée dans le cadre, pour une position de défilement réelle
 * donnée — sert uniquement à resynchroniser l'index affiché après un défilement qu'aucune
 * flèche/touche n'a déclenché (glisser au doigt, molette, `onScroll`). Strictement croissante
 * avec `scrollLeft` : avancer et reculer y sont la même formule, jamais un cas particulier.
 *
 * `offsets` est la position (`offsetLeft`, en pixels) de chaque carte dans le rail.
 */
export function indexCarteGauche(offsets: number[], scrollLeft: number): number {
  if (offsets.length === 0) return 0;
  const i = offsets.findIndex((offset) => offset >= scrollLeft - TOLERANCE_PX);
  return i === -1 ? offsets.length - 1 : i;
}

/**
 * Ramène un index visé dans les bornes `[0, total - 1]` (`0` si `total` est nul).
 *
 * Empêche les flèches ◀ ▶ et les flèches du clavier de sortir du rail : la flèche ▶ sur la
 * dernière carte, ou ◀ sur la première, ne fait rien plutôt que de viser une carte absente.
 */
export function indexBorne(total: number, index: number): number {
  if (total <= 0) return 0;
  return Math.min(total - 1, Math.max(0, index));
}
